import axios from "axios";

import api from "@/api/api_instance";
import { GET_ALL_PAGE_SIZE } from "@/constants/app.constants";
import { UserDef } from "@/features/auth/types/auth.types";
import {
  AppResponse,
  PaginationParams,
  PaginationResponse,
} from "@/types/app.types";

import { GroupSortEnum } from "../constants/group.constant";
import { GroupEndpoints } from "../constants/group.endpoints";
import {
  DeleteDeviceAuthenticationRequestParams,
  GetGroupDeviceByManagerResponse,
  GetGroupStructureListParams,
} from "../types/groupDevice.type";
// eslint-disable-next-line import/order
import {
  UpdateGroupParams,
  CreateGroupParams,
  GetGroupFriendParams,
  GetGroupInviteParams,
  GetGroupInviteResponse,
  GetGroupParams,
  GetRelationshipFriendParams,
  GetUsersOfGroupParams,
  GroupInfoDef,
  GroupInvitationRequest,
  GroupsListResponse,
  GroupParams,
  JoinRequestedDef,
  LeaveRequestDef,
  UpdateMembershipResponse,
  UpdateStatusDeviceRequestParams,
  UserRequest,
  RemoveDeviceRequestParams,
  IGroupInfoResponse,
  GroupsListRestore,
  GroupInfoDeleteResponse,
  SettingGroupParams,
  ResponseGroupInvite,
  FriendListResponse,
  GroupInvitationPersonalRequest,
  GroupPersonalRequest,
  GroupRequestResponse,
  IMemberGroup,
  DetailMemberResponse,
  GetGroupDeviceByStatusParams,
  RemoveDeviceByManagerParams,
  GetMyGroupDeviceResponse,
  RemoveDeviceByMeParams,
  VerifyRemoveDeviceByMeParams,
  UpdateGroupDeviceStatusParams,
  GroupStructureDetailDef,
  GroupDeletedDef,
  SubGroupDef,
} from "./../types/group.type";

export const searchGroup = ({
  keyword = "",
  pageNumber = 1,
  pageSize = 10,
  signal,
}: GetGroupParams): Promise<GroupsListResponse> => {
  return api.get(GroupEndpoints.SEARCH_GROUP, {
    params: {
      pageNumber,
      pageSize,
      keyword,
    },
    signal,
  });
};

export const getMyGroups = (
  params: GetGroupParams
): Promise<GroupsListResponse> => {
  return api.get(GroupEndpoints.MY_GROUP, {
    params,
  });
};

export const getDiscoverGroups = ({
  pageNumber = 1,
  pageSize = 10,
}): Promise<GroupsListResponse> => {
  return api.get(GroupEndpoints.DISCOVER_GROUP, {
    params: {
      pageNumber,
      pageSize,
    },
  });
};

export const createGroup = (
  groupInfo: CreateGroupParams
): Promise<IGroupInfoResponse> => {
  return api.post(GroupEndpoints.GROUP, groupInfo);
};

export const generatePresignedUrl = (
  file: string,
  size: number,
  groupId: number
) => {
  return api.post(GroupEndpoints.GROUP_PHOTO, {
    size,
    fileName: file,
    targetId: groupId,
    type: "GROUP",
  });
};

export const deleteGroupPhoto = (groupId: number) => {
  return api.delete(GroupEndpoints.deletePhoto(groupId));
};

export const uploadGroupPhoto = (
  file: File,
  url: string,
  typeFile: string,
  fileLength: number
) => {
  return axios.put(url, file, {
    headers: {
      "Content-Type": typeFile,
      "Content-Length": fileLength,
    },
  });
};

export const updateGroup = (
  params: UpdateGroupParams
): Promise<IGroupInfoResponse> => {
  return api.put(GroupEndpoints.GROUP, params);
};

export const settingGroup = (params: SettingGroupParams) => {
  return api.put(GroupEndpoints.SETTING_GROUP, params);
};

export const getGroupById = (id: number): Promise<IGroupInfoResponse> => {
  return api.get(GroupEndpoints.detail(id));
};

export const getConfirmInfoDelete = (
  id: number
): Promise<GroupInfoDeleteResponse> => {
  return api.get(GroupEndpoints.confirmDelete(id));
};

export const requestDeleteGroup = (
  groupId: number
): Promise<{ data: { uid?: string } }> => {
  return api.post(GroupEndpoints.DELETE_REQUEST, { groupId });
};

export type DeleteGroupVerifyParams = {
  otp: string;
  uid: string;
  username: string;
};

export const deleteGroup = (id: number, verify?: DeleteGroupVerifyParams) => {
  return api.delete(GroupEndpoints.DELETE, {
    params: { groupId: id },
    headers: verify
      ? {
          "tg-code": verify.otp,
          "tg-uuid": verify.uid,
          "tg-username": verify.username,
        }
      : undefined,
  });
};

export const deletedGroups = (id: number): Promise<GroupsListResponse> => {
  return api.get(GroupEndpoints.deletedGroups(id));
};

export const destroyGroup = (id: number) => {
  return api.delete(GroupEndpoints.destroy(id));
};

export const restoreGroup = (id: number) => {
  return api.put(GroupEndpoints.RESTORE, { groupId: id });
};

export const switchDeviceRequestMode = (id: number) => {
  return api.put(GroupEndpoints.deviceRequestMode(id));
};

export const switchLockMode = (id: number) => {
  return api.put(GroupEndpoints.lockMode(id));
};

export const switchUserRequestRequiredMode = (id: number) => {
  return api.put(GroupEndpoints.userRequestRequiredMode(id));
};

export const getGroupDeviceByStatus = (
  params: GetGroupDeviceByStatusParams
): Promise<PaginationResponse<GetGroupDeviceByManagerResponse[]>> => {
  const { groupId, pageNumber = 1, pageSize = 50, status } = params;
  return api.get(GroupEndpoints.GROUP_DEVICE, {
    params: {
      pageNumber,
      pageSize,
      groupId,
      status,
    },
  });
};

export const getDeviceGroupPersonalRequests = (groupId: number) => {
  return api.get(GroupEndpoints.DEVICE_PERSONAL_REQUEST, {
    params: {
      pageNum: 0,
      pageSize: 50,
      groupId,
    },
  });
};

export const getListFriendForGroupInvitation = (
  params: GetGroupFriendParams
): Promise<FriendListResponse> => {
  return api.get(GroupEndpoints.INVITE_FRIEND, {
    params,
  });
};

export const createInvitationToFriend = (
  groupId: number,
  ids: number[]
): Promise<FriendListResponse> => {
  return api.post(GroupEndpoints.INVITE_FRIEND, {
    groupId,
    ids,
  });
};

export const getListMemberForGroupInvitation = (
  params: GetGroupFriendParams
): Promise<FriendListResponse> => {
  return api.get(GroupEndpoints.INVITE_MEMBER, {
    params,
  });
};

export const createInvitationToMember = (
  groupId: number,
  ids: number[]
): Promise<FriendListResponse> => {
  return api.post(GroupEndpoints.INVITE_MEMBER, {
    groupId,
    ids,
  });
};

export const getGroupInvitationForUser = (params: {
  pageNumber?: number;
  pageSize?: number;
  rootGroupId?: number;
  sort?: GroupSortEnum;
}): Promise<GroupInvitationPersonalRequest> => {
  return api.get(GroupEndpoints.PERSONAL_INVITE, { params });
};
export const getGroupInvitationRootForUser = (params: {
  pageNum?: number;
  pageSize?: number;
}): Promise<GroupInvitationPersonalRequest> => {
  return api.get(GroupEndpoints.PERSONAL_INVITE_ROOT, { params });
};
export const getGroupRootGroupDeleted = (params: {
  pageNumber: number;
  pageSize: number;
  sort?: string;
}): Promise<GroupsListRestore> => {
  return api.get(GroupEndpoints.DELETED_ROOT_GROUPS, { params });
};

export const getListGroupInvited = (params: {
  groupId: number;
  pageNumber?: number;
  pageSize?: number;
}): Promise<GroupInvitationRequest> => {
  return api.get(GroupEndpoints.GROUP_INVITE, { params });
};

export const cancelGroupInvited = (
  id: number
): Promise<GroupInvitationRequest> => {
  return api.put(GroupEndpoints.GROUP_INVITE_CANCEL, { id });
};

export const respondGroupInvitation = (status: boolean, groupId: number) => {
  return api.put(GroupEndpoints.personalInviteRespond(status, groupId));
};

export const respondGroupEmailInvitation = (
  status: boolean,
  groupId: number
) => {
  return api.put(GroupEndpoints.personalEmailInviteRespond(status, groupId));
};

export const getListPersonalInvited = ({
  pageNum = 1,
  pageSize = 10,
}): Promise<GroupInfoDef[]> => {
  return api.get(GroupEndpoints.GROUP_INVITE, {
    params: {
      pageNum,
      pageSize,
    },
  });
};

export const acceptGroupInvitation = (accept: boolean, id: number) => {
  return api.put(GroupEndpoints.PERSONAL_INVITE, {
    accept: accept,
    id: id,
  });
};

export const rejectGroupInvitation = (accept: boolean, id: number) => {
  return api.put(GroupEndpoints.PERSONAL_INVITE, {
    accept: accept,
    id: id,
  });
};

export const cancelGroupInvitation = (requestId: number) => {
  return api.delete(`${GroupEndpoints.PERSONAL_INVITE}?requestId=${requestId}`);
};

export const getUsersOfGroup = (
  params: GetUsersOfGroupParams,
  signal?: AbortSignal
): Promise<PaginationResponse<UserDef[]>> => {
  const {
    pageNum = 1,
    pageSize = GET_ALL_PAGE_SIZE,
    groupId,
    role,
    keyword,
  } = params;
  return api.get(GroupEndpoints.USER, {
    params: {
      pageNum,
      pageSize,
      groupId,
      role,
      keyword,
    },
    signal,
  });
};

export const getGroupMembers = (params: {
  groupId: number;
  pageSize?: number;
  pageNumber?: number;
}): Promise<PaginationResponse<IMemberGroup[]>> => {
  return api.get(GroupEndpoints.MEMBERS_GROUP, {
    params,
  });
};

export const getDetailGroupMember = (params: {
  groupId: number;
  userId: number;
}): Promise<DetailMemberResponse> => {
  return api.get(GroupEndpoints.MEMBER_GROUP, {
    params,
  });
};

export const updateGroupMember = (
  groupId: number,
  userId: number,
  alias?: string
): Promise<DetailMemberResponse> => {
  return api.put(
    `${GroupEndpoints.MEMBER_GROUP}?alias=${alias}&groupId=${groupId}&userId=${userId}`,
    {
      alias,
      groupId,
      userId,
    }
  );
};

export const deleteGroupMember = (
  groupId: number,
  userId: number
): Promise<DetailMemberResponse> => {
  return api.delete(
    `${GroupEndpoints.MEMBER_GROUP}?groupId=${groupId}&userId=${userId}`
  );
};

export const assignRole = (groupId: number, userId: number, code: string) => {
  return api.put(GroupEndpoints.MEMBER_ROLE, {
    groupId,
    userId,
    code,
  });
};

export const getMemberTransferOwner = (params: {
  groupId: number;
  pageNumber?: number;
  pageSize?: number;
}): Promise<PaginationResponse<IMemberGroup[]>> => {
  return api.get(GroupEndpoints.TRANSFER_MEMBER, {
    params,
  });
};

export const memberTransferOwnerRequest = (params: {
  groupId: number;
  userId: number;
}): Promise<{ data: { uid?: string } }> => {
  return api.post(GroupEndpoints.TRANSFER_REQUEST, params);
};

export const memberTransferOwnerVerify = (params: {
  groupId: number;
  userId: number;
  uid: string;
  otp: string;
}) => {
  const { otp, uid, groupId, userId } = params;
  return api.post(
    GroupEndpoints.TRANSFER_ACCEPT,
    { groupId, userId },
    {
      headers: {
        "tg-code": otp,
        "tg-uuid": uid,
      },
    }
  );
};

export const createLeaveRequest = (groupId: number, rootGroupId: number) => {
  return api.post(GroupEndpoints.LEAVE_REQUEST, {
    groupId,
    rootGroupId,
  });
};

export const userCancelRequestToLeaveGroup = (groupId: number) => {
  return api.delete(GroupEndpoints.LEAVE_REQUEST, {
    params: {
      groupId,
    },
  });
};

export const userRequestToGroup = (groupId: number) => {
  return api.post(GroupEndpoints.USER_REQUEST_TO_GROUP, {
    groupId: groupId,
  });
};

export const userCancelRequestToGroup = (groupId: number) => {
  return api.put(GroupEndpoints.USER_CANCEL_REQUEST_TO_GROUP, {
    id: groupId,
  });
};

export const deleteUserFromGroup = (groupId: number, userIds: number[]) => {
  return api.put(GroupEndpoints.USER, { groupId, userIds });
};

export const getUserRequests = (params: {
  pageNumber?: number;
  pageSize?: number;
  rootGroupId?: number;
  sort?: GroupSortEnum;
}): Promise<GroupPersonalRequest> => {
  return api.get(GroupEndpoints.USER_REQUEST, {
    params,
  });
};
export const getGroupRequestRootForUser = (params: {
  pageNum?: number;
  pageSize?: number;
}): Promise<GroupPersonalRequest> => {
  return api.get(GroupEndpoints.USER_REQUEST_ROOT, { params });
};
export const getRequestsOfGroup = (params: {
  pageNumber?: number;
  pageSize?: number;
  groupId: number;
}): Promise<GroupRequestResponse> => {
  const { groupId, pageNumber = 1, pageSize = 10 } = params;
  return api.get(GroupEndpoints.GROUP_REQUEST, {
    params: {
      pageNumber,
      pageSize,
      groupId,
    },
  });
};

export const getInviteRequests = (
  params: GroupParams
): Promise<JoinRequestedDef[]> => {
  const { groupId, pageNum = 1, pageSize = 10 } = params;
  return api.get(GroupEndpoints.GROUP_REQUEST, {
    params: {
      pageNum,
      pageSize,
      groupId,
    },
  });
};

export const updateStatusJoinRequest = (params: {
  id: number;
  groupId: number;
  accept: boolean;
}) => {
  return api.put(GroupEndpoints.UPDATE_REQUEST_TO_GROUP, params);
};

export const getMyGroupRequests = (
  params: PaginationParams
): Promise<UserRequest[]> => {
  return api.get(GroupEndpoints.USER_REQUEST_PERSONAL, {
    params,
  });
};

export const getGroupTreeMap = (
  groupId: string,
  pageNum = 1,
  pageSize = GET_ALL_PAGE_SIZE
): Promise<PaginationResponse<GroupStructureDetailDef[]>> => {
  return api.get(GroupEndpoints.TREE_MAP, {
    params: {
      groupId,
      pageNum,
      pageSize,
    },
  });
};
export const getGroupStructureLisst = (
  params: GetGroupStructureListParams
): Promise<PaginationResponse<GroupStructureDetailDef[]>> => {
  return api.get(GroupEndpoints.GROUP_STRUCTURES, {
    params: params,
  });
};
export const createInvitation = (
  groupId: number,
  emails: string[]
): Promise<ResponseGroupInvite> => {
  return api.post(GroupEndpoints.INVITE, {
    groupId,
    emails,
  });
};

export const getRelationshipFriend = ({
  pageNum = 1,
  pageSize = 50,
  name = "",
  groupId,
  signal,
}: GetRelationshipFriendParams): Promise<PaginationResponse<UserDef[]>> => {
  return api.get(GroupEndpoints.RELATIONSHIP_FRIEND, {
    params: {
      pageNum,
      pageSize,
      keyword: name || undefined,
      groupId,
    },
    signal,
  });
};

export const updateStatusDeviceRequest = (
  params: UpdateStatusDeviceRequestParams
) => {
  return api.put(GroupEndpoints.DEVICE, params);
};

export const removeDevices = (params: RemoveDeviceRequestParams) => {
  return api.put(GroupEndpoints.REMOVE_DEVICE, params);
};

export const getLeaveRequests = (params: {
  groupId: number;
  pageNumber?: number;
  pageSize?: number;
}): Promise<PaginationResponse<LeaveRequestDef[]>> => {
  return api.get(GroupEndpoints.LEAVE_REQUEST, {
    params,
  });
};

export const updateStatusLeaveRequest = (params: {
  userId: number;
  groupId: number;
  accept: boolean;
}): Promise<{ id: string; status: string }> => {
  return api.put(GroupEndpoints.LEAVE_REQUEST, params);
};

export const cancelStatusLeaveRequest = (
  groupId: number,
  rootGroupId: number
) => {
  return api.delete(GroupEndpoints.LEAVE_REQUEST, {
    data: {
      groupId,
      rootGroupId,
    },
  });
};

export const updateMember = async (
  params: UpdateMembershipResponse
): Promise<UpdateMembershipResponse> => {
  return api.put(GroupEndpoints.MEMBERS_GROUP, params);
};

export const getGroupInvite = (
  params: GetGroupInviteParams
): Promise<GetGroupInviteResponse> => {
  return api.get(GroupEndpoints.INVITE, {
    params,
  });
};

export const sendRequestDeviceVerify = (params: { groupId: number }) => {
  return api.post(GroupEndpoints.DEVICE, params);
};

export const verifyDevice = (params: {
  groupId: number;
  otp: string;
  uuid: string;
}) => {
  const { groupId, otp, uuid } = params;
  return api.post(
    GroupEndpoints.DEVICE_VERIFY,
    {
      groupId,
    },
    {
      headers: {
        "tg-code": otp,
        "tg-uuid": uuid,
      },
    }
  );
};

export const cancelDeviceRequest = (groupId: number) => {
  return api.delete(GroupEndpoints.deviceCancelRequest(groupId));
};

export const removeGroupDeviceByManager = (
  params: RemoveDeviceByManagerParams
) => {
  return api.post(GroupEndpoints.GROUP_DEVICE_REMOVE, params);
};

export const getMyGroupDevice = (
  groupId: number
): Promise<AppResponse<GetMyGroupDeviceResponse>> => {
  return api.get(GroupEndpoints.MY_GROUP_DEVICE, {
    params: {
      groupId,
    },
  });
};

export const removeMyDevice = (
  params: RemoveDeviceByMeParams
): Promise<
  AppResponse<{
    uid: string;
  }>
> => {
  return api.post(GroupEndpoints.REMOVE_MY_DEVICE, params);
};

export const verifyRemoveMyDevice = (
  params: VerifyRemoveDeviceByMeParams
): Promise<AppResponse<boolean>> => {
  const { deviceUniqueId, groupId, code, uid, userId } = params;
  return api.post(
    GroupEndpoints.VERIFY_REMOVE_MY_DEVICE,
    {
      deviceUniqueId,
      groupId,
    },
    {
      headers: {
        "tg-code": code,
        "tg-uuid": uid,
        "tg-userid": userId,
      },
    }
  );
};

export const updateGroupDeviceStatus = (
  params: UpdateGroupDeviceStatusParams
): Promise<AppResponse<boolean>> => {
  return api.put(GroupEndpoints.UPDATE_GROUP_DEVICE_STATUS, params);
};

export const cancelDeviceAuthenticationRequest = (
  params: DeleteDeviceAuthenticationRequestParams
): Promise<AppResponse<boolean>> => {
  return api.delete(GroupEndpoints.CANCEL_DEVICE_AUTHENTICATION_REQUEST, {
    data: params,
  });
};

export const getGroupStructureDetail = (
  groupId: number
): Promise<AppResponse<GroupStructureDetailDef>> => {
  return api.get(GroupEndpoints.GROUP_STRUCTURES_DETAIL, {
    params: {
      groupId,
    },
  });
};

export const getGroupStructureList = (
  params: GetGroupStructureListParams
): Promise<PaginationResponse<GroupStructureDetailDef[]>> => {
  return api.get(GroupEndpoints.GROUP_STRUCTURES, {
    params: params,
  });
};

export const getGroupDeletedList = (
  params: {
    groupId: number;
  } & PaginationParams
): Promise<PaginationResponse<GroupDeletedDef[]>> => {
  return api.get(GroupEndpoints.DELETE_SUB_GROUP, {
    params: params,
  });
};

export const recoverGroup = (params: {
  recoveryGroupId: number;
  requestGroupId: number;
}): Promise<AppResponse<boolean>> => {
  return api.put(GroupEndpoints.RECOVER_GROUP, params);
};

export const deleteSubGroup = (
  groupId: number,
  verify?: DeleteGroupVerifyParams
): Promise<AppResponse<boolean>> => {
  return api.delete(GroupEndpoints.DELETE_SUB_GROUP, {
    params: {
      groupId,
    },
    headers: verify
      ? {
          "tg-code": verify.otp,
          "tg-uuid": verify.uid,
          "tg-username": verify.username,
        }
      : undefined,
  });
};

export const getSubGroup = (
  groupId: number
): Promise<PaginationResponse<SubGroupDef[]>> => {
  return api.get(GroupEndpoints.SUB_GROUP, {
    params: {
      id: groupId,
      pageSize: GET_ALL_PAGE_SIZE,
    },
  });
};

export const toggleNotification = (
  groupId: number,
  isNotified: boolean
): Promise<AppResponse<boolean>> => {
  return api.put(GroupEndpoints.TOGGLE_NOTIFY, { groupId, isNotified });
};

export const pinGroup = (groupId: number): Promise<AppResponse<boolean>> => {
  return api.post(GroupEndpoints.pin(groupId));
};

export const unpinGroup = (groupId: number): Promise<AppResponse<boolean>> => {
  return api.delete(GroupEndpoints.pin(groupId));
};
